import Paginator, { usePaginationLocal } from "components/Shared/Paginator"
import React, { useEffect, useState } from "react"
import {
	ID_INFORMATION_TABLE,
	DEFAULT_SORTING,
	ITEMS_PER_PAGE,
	BREAKDOWN_METRIC,
} from "../../constants"
import { SalesDetails } from "../../interfaces"
import {
	configurateColumnsHeaderBenchmark,
	getBarCellMax,
	translatePresetToRange,
} from "../../utils/utils"
import InformationTable from "components/Shared/InformationTable"
import GenerateRowsTable from "../GenerateRowsTable"
import { BenchmarkTabProps } from "./interfaces"
import { useIntl } from "react-intl"
import Legend from "../Legend/Legend"
import { useGetInformationBenchmark } from "../../hooks/useGetInformationBenchmark"
import SkeletonWrapper from "components/Shared/SkeletonWrapper"
import BenchmarkTableSkeleton from "../BenchmarkTableSkeleton"
import { DAILY_INTERVAL } from "components/Shared/Charts/Timeline/constant"
import AsyncMessageResponseHandler from "components/Shared/AsyncMessageResponseHandler"
import { getMetricNameForInsight } from "../MetricsSelect/utils"
import { ExportCSV } from "components/Shared/ExportCSV"
import "./index.scss"
import { getExportDataFileName, mapDataToExportFormat } from "./utils"
import { useFiltersContext } from "components/Shared/GlobalFilter/context/filtersContext"

const BenchmarkTab: React.FC<BenchmarkTabProps> = (props) => {
	const intl = useIntl()
	const {
		filtersBenchmark,
		setLevelName,
		levelName,
		metricName,
		hasPaginator,
		isPenetrationMetric = false,
	} = props
	const [dataTable, setDataTable] = useState<Array<SalesDetails>>([])
	const {
		moduleState: {
			transactional: {
				benchmark: { metric: selectedMetric },
			},
		},
	} = useFiltersContext()

	const metric = getMetricNameForInsight(
		filtersBenchmark.metricName,
		BREAKDOWN_METRIC,
		metricName
	)

	const { period: translatePeriod } = translatePresetToRange(filtersBenchmark)

	const debouncedFilters = {
		...filtersBenchmark,
		metricName: metric,
		interval: DAILY_INTERVAL,
		period: translatePeriod,
		selectedMetric,
	}

	const {
		dataTableBenchmark,
		isPendingRequest,
		isError,
		error,
		refetch: retry,
	} = useGetInformationBenchmark(debouncedFilters)

	useEffect(() => {
		if (dataTableBenchmark) {
			handleSortDataTable(
				dataTableBenchmark?.commercialBenchmarkBreakdown?.data ?? []
			)
			setLevelName(dataTableBenchmark?.commercialBenchmarkBreakdown?.levelName)
		}
	}, [dataTableBenchmark])

	const { currentData, getPaginatorProps, setSelectPage } = usePaginationLocal({
		data: dataTable,
		itemsPerPage: ITEMS_PER_PAGE,
	})

	const handleSortDataTable = (dataTable: Array<SalesDetails>) => {
		setSelectPage(0)
		setDataTable(dataTable)
	}

	const displayMetricName = isPenetrationMetric
		? "penetration"
		: filtersBenchmark.metricName

	return (
		<SkeletonWrapper
			isLoading={isPendingRequest}
			skeleton={<BenchmarkTableSkeleton />}
		>
			<AsyncMessageResponseHandler
				hasData={Boolean(
					dataTableBenchmark?.commercialBenchmarkBreakdown?.data
				)}
				error={error}
				isLoading={isPendingRequest}
				isError={isError}
				retry={retry}
			>
				<div className="csv-button-container">
					<ExportCSV
						fileName={getExportDataFileName(displayMetricName, levelName, intl)}
						data={mapDataToExportFormat(dataTable)}
						name={"phronesis.transactional.application.cross-selling.download"}
						align={"right"}
					/>
				</div>
				<div className="container-table-benchmark">
					<InformationTable
						id={ID_INFORMATION_TABLE}
						data={dataTable}
						onSort={handleSortDataTable}
						columnsHeader={configurateColumnsHeaderBenchmark(
							levelName,
							filtersBenchmark,
							intl,
							isPenetrationMetric,
							metricName
						)}
						hasSorting
						defaultSorting={DEFAULT_SORTING}
						rowsBody={
							<GenerateRowsTable
								data={currentData}
								maxValue={getBarCellMax(dataTable)}
								period={filtersBenchmark.period}
								isPenetrationMetric={isPenetrationMetric}
								filtersBenchmark={filtersBenchmark}
								metricName={metricName}
							/>
						}
						contentFooterTable={
							<>
								<Legend filtersBenchmark={filtersBenchmark} />
								{hasPaginator && <Paginator {...getPaginatorProps()} />}
							</>
						}
					/>
				</div>
			</AsyncMessageResponseHandler>
		</SkeletonWrapper>
	)
}

export default BenchmarkTab
